import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ViewChild, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MenuComponent } from '../shared/menu/menu.component';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../services/auth.service';
import { tap, timer, Observable, filter, distinctUntilChanged, map } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LayoutService } from './layout.service';

type FlyoutKey = 'files' | 'products';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, MenuComponent, MatSidenavModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, OverlayModule],
  providers: [LayoutService],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss'
})
export class LayoutComponent {
  @ViewChild('drawer') drawer?: MatDrawer;
  client: number | null = null;
  rol: boolean = false
  // logoUrl: string = 'assets/small-icon.svg'; // Logo por defecto
  iconUrl!: string
  logoUrl!: string
  auth = inject(AuthService)
  isMobile = false;
  private breakpointObserver = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);
  private layoutService = inject(LayoutService);
  private router = inject(Router);

  constructor(private cdRef: ChangeDetectorRef) {

  }
  iconview: boolean = true
  user$!: Observable<any>;
  ngOnInit() {
    this.breakpointObserver
      .observe(['(max-width: 900px)'])
      .pipe(
        map(state => state.matches),
        distinctUntilChanged(),
        tap(isMobile => {
          this.isMobile = isMobile;
          this.cdRef.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
    this.layoutService.menuToggle$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.drawer?.toggle();
      });
    this.layoutService.menuClose$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.drawer?.close();
      });
    this.destroyRef.onDestroy(() => this.cancelCloseFlyout());
    // Suscríbete al usuario autenticado
    this.user$ = this.auth.userData$.pipe(
      filter((d): d is { client_id: number, rol: boolean } => !!d),
      tap(d => {
        // console.log('Datos del usuario:', d);
        this.client = d.client_id;
        this.rol = d.rol;
        this.setLogo(this.client);
        this.cdRef.detectChanges();
        // Oculta el icono después de 2 segundos
        timer(500).subscribe(() => {
          this.iconview = false;
        });
      })
    );

  }

  private setLogo(client_id: number | null) {
    // console.log('client_id', client_id)
    switch (client_id) {
      case 7:
        this.iconUrl = 'assets/icon-pepsi.svg';
        this.logoUrl = 'assets/logo-pepsi.svg';
        break;
      case 2:
        this.iconUrl = 'assets/small-icon.svg';
        this.logoUrl = 'assets/small-icon.svg';
        break;
      case 5:
        this.iconUrl = 'assets/logo-alpina.svg';
        this.logoUrl = 'assets/logo-alpina.svg';
        break;
      default:
        this.logoUrl = 'default-logo.png';
        break;
    }
  }

  signOut() {
    this.auth.signOut()
  }

  closeIfMobile() {
    if (this.isMobile) {
      this.drawer?.close();
    }
  }

  openFlyoutKey: FlyoutKey | null = null;
  private flyoutCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  // Ancla el panel al lado derecho del ícono, centrado verticalmente,
  // con un pequeño offset para no tocar el trigger (evita el parpadeo de hover).
  readonly flyoutPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 10 },
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 10 },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 10 }
  ];

  openFlyout(key: FlyoutKey): void {
    this.cancelCloseFlyout();
    this.openFlyoutKey = key;
  }

  scheduleCloseFlyout(): void {
    this.cancelCloseFlyout();
    this.flyoutCloseTimeout = setTimeout(() => {
      this.openFlyoutKey = null;
    }, 200);
  }

  cancelCloseFlyout(): void {
    if (this.flyoutCloseTimeout) {
      clearTimeout(this.flyoutCloseTimeout);
      this.flyoutCloseTimeout = null;
    }
  }

  isGestionArchivosActive(): boolean {
    const url = this.getCleanUrl();
    return url === '/schemas' || url.startsWith('/collector-datasets');
  }

  isSchemasAdminActive(): boolean {
    const url = this.getCleanUrl();
    return (
      url.startsWith('/schemas-admin') ||
      url.startsWith('/schemas/create') ||
      /^\/schemas\/[^/]+\/edit$/.test(url)
    );
  }

  isProductsActive(): boolean {
    const url = this.getCleanUrl();
    return url.startsWith('/library-attributes') || url.startsWith('/library-objects');
  }

  isSchemaRulesActive(): boolean {
    const url = this.getCleanUrl();
    return url.startsWith('/schema-rules');
  }

  private getCleanUrl(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }
}
